import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Multiselect,
  Select,
  SelectProps,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react-v3";

import constants from "../../../../constants";
import {
  createTestplan,
  getLatestTestplan,
  getTestplanConfig,
  resetTestplanOnFailure,
  resetTestplanOnSuccess,
} from "../../../../redux/actions/testplan-action";
import {
  downloadAction,
  resetDownload,
} from "../../../../redux/actions/download-action";
import { createNotification} from "../../../../redux/actions/notifications-action";
import { parseS3Link } from "../../../../utils/s3-utils";
import {
  getTestPlanChildrenOptions,
  getTestPlanOptions,
} from "../../../../utils/general-utils";
import {
  COUNTRY_TESTPLAN_MAP,
  TECHNOLOGY_TESTPLAN_MAP,
  antennaDropDown,
} from "../../../../utils/constants";
import TestPlanTable from "./test-plan-table";
import ResponseModal from "./response-modal";

const TestPlanRF = ({
  formMetadata,
  selectedTechnologies,
  selectedCountries,
}) => {
  const dispatch = useDispatch();
  const {
    downloadReducer: { loadingStatus: downloadLoadingStatus, url: downloadUrl },
    testplanReducer: {
      techSpec,
      countries,
      createStatus,
      responseData,
      message,
      loadingLatestTestplanStatus,
      latestTestplanData,
    },
  } = useSelector((state: any) => state);

  useEffect(() => {
    if (!Object.keys(techSpec || {}).length) dispatch(getTestplanConfig());
  }, [dispatch, techSpec]);

  const [selectedTechnology, setSelectedTechnology] =
    useState<SelectProps.Option | null>(null);
  const [selectedSpecifications, setSelectedSpecifications] = useState<
    SelectProps.Option[]
  >([]);
  const [selectedAntenna, setSelectedAntenna] =
    useState<SelectProps.Option | null>(null);
  const [minPower, setMinPower] = useState<string>("");
  const [internalMargin, setInternalMargin] = useState<string>("");
  const [errors, setErrors] = useState<any>({});
  const [hasDuplicateRecord, setHasDuplicateRecord] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const [showDownload, setShowDownload] = useState(false);

  const selectedCountryIds = useMemo(() => {
    return Object.keys(countries)
      .filter((key) => {
        const country = countries[key].name;
        return Object.keys(COUNTRY_TESTPLAN_MAP).some(
          (selectedCountry) =>
            COUNTRY_TESTPLAN_MAP[selectedCountry] === country &&
            selectedCountries.includes(selectedCountry)
        );
      })
      .map(Number);
  }, [countries, selectedCountries, COUNTRY_TESTPLAN_MAP]);

  const clearError = (fieldName: string) => {
    setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
    setHasDuplicateRecord(false);
  };

  const technologyOptions = useMemo(() => {
    const testPlanTechOptions = getTestPlanOptions(techSpec);
    return testPlanTechOptions.filter((option) =>
      selectedTechnologies.some(
        (technology) => TECHNOLOGY_TESTPLAN_MAP[technology] === option.label
      )
    );
  }, [techSpec, selectedTechnologies, TECHNOLOGY_TESTPLAN_MAP]);

  const handleTechnologySelect = ({ detail }) => {
    clearError("technology");
    setSelectedTechnology(detail.selectedOption);
    setSelectedSpecifications([]);
  };

  const specificationOptions = useMemo(
    () => getTestPlanChildrenOptions(techSpec, selectedTechnology?.value || ""),
    [techSpec, selectedTechnology]
  );
  const handleSpecificationsSelect = ({ detail }) => {
    clearError("specifications");
    setSelectedSpecifications(detail.selectedOptions);
  };

  const handleAntennaSelect = ({ detail }) => {
    clearError("antenna");
    setSelectedAntenna(detail.selectedOption);
  };

  const handleMinPowerChange = ({ detail }) => {
    clearError("minPower");
    setMinPower(detail.value);
  };

  const handleInternalMarginChange = ({ detail }) => {
    clearError("internalMargin");
    setInternalMargin(detail.value);
  };

  const clearSelection = () => {
    setSelectedTechnology(null);
    setSelectedSpecifications([]);
    setSelectedAntenna(null);
    setMinPower("");
    setInternalMargin("");
    setErrors({});
    setHasDuplicateRecord(false);
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};
    const errorMessage = "Required field";

    if (!selectedTechnology) newErrors.technology = errorMessage;
    if (!selectedSpecifications.length) newErrors.specifications = errorMessage;
    if (!selectedAntenna) newErrors.antenna = errorMessage;
    if (!minPower.trim()) newErrors.minPower = errorMessage;
    if (!internalMargin.trim()) newErrors.internalMargin = errorMessage;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddSelection = () => {
    if (!validateForm()) return;

    const data = {
      selectedSpecifications,
      selectedTechnology,
      selectedAntenna,
      minPower,
      internalMargin
    };
    setFormData(data);
  };

  const handleFormDataEntry = ({ isDuplicate }) => {
    setHasDuplicateRecord(isDuplicate);
    !isDuplicate && clearSelection();
  };

  const handleSubmit = (tableItems) => {
    if (!tableItems?.length || !preFormDataExists) return;

    const { categoryId, projectId, buildId, buildConfigId } = formMetadata;
    const requestPayload = {
      countries: selectedCountryIds,
      tech_specs: tableItems.map((item) => ({
        technology_id: +(item.technologyOption?.value || ""),
        tech_spec_id: +(item.specificationOption?.value || ""),
        antenna: item.antennaOption?.value || "",
        minimum_power: item.minPower,
        internal_margin: item.internalMargin,
      })),
    };

    dispatch(
      createTestplan(
        categoryId,
        projectId,
        buildId,
        buildConfigId,
        requestPayload
      )
    );
  };

  const handleDismissModal = (success: boolean) => {
    if (success) {
      clearSelection();
      setShowDownload(true);
      dispatch(resetTestplanOnSuccess());
    } else {
      dispatch(resetTestplanOnFailure());
    }
  };

  const handleDownloadTestplan = () => {
    const { categoryId, projectId, buildId, buildConfigId } = formMetadata;
    dispatch(getLatestTestplan(categoryId, projectId, buildId, buildConfigId));
  };

  const loadedLatestTestPlan =
    loadingLatestTestplanStatus === constants.LOADING_SUCCESS;
  useEffect(() => {
    if (!loadedLatestTestPlan || !latestTestplanData?.s3_link) return;

    const { file_path, object_key, bucket_name, version_id } = parseS3Link(
      latestTestplanData.s3_link
    );
    const data = { file_name: file_path, object_key, bucket_name, version_id };
    dispatch(downloadAction(data));
  }, [dispatch, loadingLatestTestplanStatus, latestTestplanData]);

  useEffect(() => {
    const downloadSuccess = downloadLoadingStatus === constants.LOADING_SUCCESS;
    if (!loadedLatestTestPlan || !downloadSuccess || !downloadUrl) return;

    window.open(downloadUrl, "_blank", "noopener,noreferrer");
    dispatch(resetTestplanOnSuccess());
    dispatch(resetDownload());
  }, [
    dispatch,
    loadingLatestTestplanStatus,
    downloadLoadingStatus,
    downloadUrl,
  ]);

  const preFormDataExists =
    selectedTechnologies?.length > 0 &&
    selectedCountries?.length > 0 &&
    Object.keys(formMetadata || {}).length > 0 &&
    Boolean(formMetadata.buildConfigId);
  const loading = preFormDataExists && !technologyOptions?.length;
  const loadingFailed = !preFormDataExists;
  const loadingSuccess = preFormDataExists && technologyOptions?.length > 0;
  const hasAddedRecord = Object.keys(formData || {}).length > 0;

  const loadingDownloadButton =
    loadingLatestTestplanStatus === constants.LOADING_LOAD ||
    downloadLoadingStatus === constants.LOADING_LOAD;
  const downloadFailed =
    loadingLatestTestplanStatus === constants.LOADING_FAIL ||
    downloadLoadingStatus === constants.LOADING_FAIL;

  return (
    <Container header={<Header variant="h2">RF Test Plan</Header>}>
      {loading && <StatusIndicator type="loading">loading</StatusIndicator>}

      {loadingFailed && (
        <StatusIndicator type="error">
          Please confirm the list of technologies and countries identified from
          BRD
        </StatusIndicator>
      )}

      {loadingSuccess && (
        <>
          <ColumnLayout columns={4}>
            <FormField label="Technology" errorText={errors.technology}>
              <Select
                selectedOption={selectedTechnology}
                onChange={handleTechnologySelect}
                options={technologyOptions}
                placeholder="Technology"
              />
            </FormField>

            <FormField label="Specification" errorText={errors.specifications}>
              <Multiselect
                selectedOptions={selectedSpecifications}
                onChange={handleSpecificationsSelect}
                options={specificationOptions}
                placeholder="Specifications"
                disabled={!selectedTechnology}
              />
            </FormField>

            <FormField label="Antenna" errorText={errors.antenna}>
              <Select
                selectedOption={selectedAntenna}
                onChange={handleAntennaSelect}
                options={antennaDropDown}
                placeholder="Antenna"
              />
            </FormField>

            <FormField label="Min Power" errorText={errors.minPower}>
              <Input
                placeholder="Enter Min Power Setting allowed"
                value={minPower}
                onChange={handleMinPowerChange}
              />
            </FormField>
            <FormField label="Internal Margin" errorText={errors.internalMargin}>
              <Input
                  placeholder="Enter Internal Margin"
                  value={internalMargin}
                  onChange={handleInternalMarginChange}
              />
            </FormField>
          </ColumnLayout>

          <br />
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="normal" onClick={clearSelection}>
              Clear
            </Button>
            <Button variant="primary" onClick={handleAddSelection}>
              Add
            </Button>
          </SpaceBetween>

          {hasDuplicateRecord && (
            <StatusIndicator type="error">
              Duplicate Entry. The combination of Technology, Specification and
              Antenna should always be unique.
            </StatusIndicator>
          )}

          {hasAddedRecord && (
            <>
              <br />
              <TestPlanTable
                formData={formData}
                onFormDataEntry={handleFormDataEntry}
                onSubmit={handleSubmit}
                submitStatus={createStatus}
                setShowDownload={setShowDownload}
              />
            </>
          )}

          {createStatus === constants.LOADING_LOAD && (
            <>
              <br />
              <StatusIndicator type="loading">
                Creating Testplan
              </StatusIndicator>
            </>
          )}
          {showDownload && (
            <Box margin={{ top: "l" }}>
              <SpaceBetween direction="horizontal" size="l">
                <Button
                  variant="primary"
                  iconName="download"
                  onClick={handleDownloadTestplan}
                  disabled={loadingDownloadButton}
                  loading={loadingDownloadButton}
                >
                  Download Testplan
                </Button>

                {downloadFailed && (
                  <StatusIndicator type="error">
                    Error while downloading Testplan
                  </StatusIndicator>
                )}
              </SpaceBetween>
            </Box>
          )}

          <ResponseModal
            createStatus={createStatus}
            message={message}
            onDismiss={handleDismissModal}
            responseData={responseData}
          />
        </>
      )}
    </Container>
  );
};

export default TestPlanRF;
